import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { ImportMeshAsync } from "@babylonjs/core/Loading/sceneLoader";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Scene } from "@babylonjs/core/scene";

// Effet de bord requis : sans lui, `moveWithCollisions` n'a pas de coordinateur
// de collisions à qui s'adresser et le personnage traverse tout.
import "@babylonjs/core/Collisions/collisionCoordinator";

import { describeError, logger } from "@/utils/logger";

/** Les quatre animations que le GLB doit fournir, nommées exactement ainsi. */
export const CHARACTER_ANIMATIONS = ["Idle", "Walk", "Run", "Chop"] as const;

export type CharacterAnimation = (typeof CHARACTER_ANIMATIONS)[number];

/** Animations cycliques : tout sauf l'action ponctuelle de coupe. */
export type LoopedAnimation = Exclude<CharacterAnimation, "Chop">;

/** Les quatre groupes d'animation du personnage, indexés par nom. */
export type CharacterAnimations = Readonly<Record<CharacterAnimation, AnimationGroup>>;

export interface CharacterInput {
  /**
   * Déplacement souhaité, en espace monde. Seul le plan horizontal compte.
   *
   * La **norme** dose la vitesse dans `[0, 1]` — c'est ce qui rend le joystick
   * progressif — et elle est plafonnée à 1 : une entrée plus longue ne fait pas
   * courir plus vite que la vitesse demandée.
   */
  readonly direction: Vector3;
  /** Commande de course : bascule `Walk` en `Run` et double la vitesse. */
  readonly running: boolean;
  /**
   * Multiplicateur de vitesse, `1` par défaut.
   *
   * Il s'applique **par-dessus** la marche comme la course, et sert à l'élan
   * pris au bout de quelques secondes d'avance ininterrompue. Le personnage ne
   * le décide pas : c'est une règle de jeu, et elle appartient à la scène.
   */
  readonly speedScale?: number;
  /**
   * Cap imposé, en radians (0 = regard vers +Z).
   *
   * Absent, le personnage regarde là où il va. Un contrôleur qui pilote lui-même
   * le cap — conduite au manche unique, où l'axe horizontal fait pivoter — le
   * fournit : sans lui, reculer ferait pivoter le personnage de 180° au lieu de
   * lui faire faire un pas en arrière.
   */
  readonly facing?: number;
}

export interface ForestBuilderOptions {
  /**
   * Fichier à charger : une URL servie par le jeu, ou les octets du GLB.
   *
   * La seconde forme n'existe que pour les tests, qui lisent le fichier sur
   * disque : Babylon accepte indifféremment les deux.
   */
  readonly source: string | ArrayBufferView;
  /** Vitesse de marche, en m/s. Cadence nominale de l'animation `Walk`. */
  readonly walkSpeed: number;
  /** Vitesse de course, en m/s. Cadence nominale de l'animation `Run`. */
  readonly runSpeed: number;
  /** Hauteur du volume de collision, en mètres. */
  readonly capsuleHeight: number;
  /** Rayon du volume de collision, en mètres. */
  readonly capsuleRadius: number;
  /**
   * Vitesse de fondu entre deux animations, dans `]0, 1]`.
   *
   * Sans fondu, passer de `Idle` à `Run` remplace la pose d'une image à l'autre :
   * les membres sautent d'une position à une autre. La valeur est une fraction
   * de l'écart rattrapée par image — 0,12 donne un enchaînement d'environ trois
   * images à 60 Hz, assez court pour rester réactif.
   */
  readonly blendingSpeed: number;
}

/** Contrat du GLB, repris de son `manifest.json`. */
export const DEFAULT_FOREST_BUILDER_OPTIONS: ForestBuilderOptions = {
  source: "/models/forest-builder-v2.glb",
  walkSpeed: 2.2,
  runSpeed: 4.4,
  capsuleHeight: 2.0,
  capsuleRadius: 0.3,
  blendingSpeed: 0.12,
};

/**
 * En deçà de cette norme, l'entrée est considérée nulle.
 *
 * Le joystick a déjà sa zone morte ; ce seuil-ci ne protège que du résidu
 * numérique, d'où une valeur très basse.
 */
const MOVE_EPSILON = 1e-4;

/**
 * Cadence minimale des cycles de marche et de course.
 *
 * La cadence suit la vitesse réelle pour que les pieds ne glissent pas sur le
 * sol : à mi-manche, le personnage avance deux fois moins vite et son cycle se
 * déroule deux fois moins vite. Sans plancher, une commande très faible figerait
 * le personnage dans une pose au lieu de le faire marcher lentement.
 */
const MIN_CADENCE = 0.35;

/**
 * Retrouve les quatre animations du personnage parmi les groupes importés.
 *
 * Échoue avec le nom manquant plutôt que de laisser le contrôleur démarrer avec
 * un groupe absent : le défaut se verrait sinon des heures plus tard, sous la
 * forme d'un personnage figé dans une pose sans erreur en console.
 */
export function collectAnimations(groups: readonly AnimationGroup[]): CharacterAnimations {
  const find = (name: CharacterAnimation): AnimationGroup => {
    const group = groups.find((candidate) => candidate.name === name);
    if (group) return group;

    const found = groups.map((candidate) => candidate.name).join(", ");
    throw new Error(
      `Personnage Forest Builder : animation « ${name} » absente du GLB ` +
        `(présentes : ${found || "aucune"}).`,
    );
  };

  return { Idle: find("Idle"), Walk: find("Walk"), Run: find("Run"), Chop: find("Chop") };
}

/**
 * Le personnage jouable : un volume de collision que le jeu déplace, et un
 * modèle animé qui le suit.
 *
 * **Les animations du GLB sont sur place** — aucune ne comporte de déplacement
 * global. Le mouvement vient donc entièrement d'ici, et les cycles ne servent
 * qu'à l'habiller ; c'est ce qui permet de doser la vitesse à la volée sans que
 * le personnage patine ou décolle.
 *
 * La collision passe par un mesh **sans géométrie** : Babylon ne collisionne pas
 * avec la forme d'un maillage porteur mais avec son `ellipsoid`, une capsule
 * implicite. Modéliser une vraie capsule ajouterait des sommets qui ne seraient
 * jamais dessinés — l'ellipsoïde suffit, et les sous-maillages visuels restent
 * hors du système de collision comme du test de sélection.
 */
export class ForestBuilderCharacter {
  /** Volume de collision, et parent de tout le reste : c'est lui qu'on déplace. */
  readonly controller: Mesh;
  /** Racine du modèle importé. Le jeu s'en sert pour projeter son ombre. */
  readonly visualRoot: AbstractMesh;

  private readonly animations: CharacterAnimations;
  private readonly options: ForestBuilderOptions;
  /** Tampon de déplacement, réutilisé d'une image à l'autre. */
  private readonly step = Vector3.Zero();

  private current: CharacterAnimation = "Idle";
  private chopping = false;
  private disposed = false;

  private constructor(
    controller: Mesh,
    visualRoot: AbstractMesh,
    animations: CharacterAnimations,
    options: ForestBuilderOptions,
  ) {
    this.controller = controller;
    this.visualRoot = visualRoot;
    this.animations = animations;
    this.options = options;

    for (const group of Object.values(animations)) {
      // Le chargeur glTF démarre d'office le premier groupe : on reprend la main
      // avant la première image, sinon `Idle` tourne sans que le contrôleur le
      // sache et le premier changement d'état superpose deux animations.
      group.stop(true);
      group.enableBlending = true;
      group.blendingSpeed = options.blendingSpeed;
    }

    // Démarrage explicite plutôt que par `playLoop` : l'état de départ étant
    // déjà « Idle », la méthode croirait le cycle en cours et ne le lancerait
    // jamais — le personnage resterait figé dans sa pose de repos.
    const { Idle } = animations;
    Idle.start(true, 1, Idle.from, Idle.to, false);
  }

  /**
   * Charge le GLB et construit le personnage.
   *
   * Le chargement du format glTF n'est pas importé ici : `main.ts` enregistre
   * les fabriques de loaders au démarrage (`registerBuiltInLoaders`), et leur
   * code n'est téléchargé qu'à ce premier appel. Un import statique le ferait
   * entrer dans le bundle initial, que la scène charge un modèle ou non.
   */
  static async load(
    scene: Scene,
    partialOptions: Partial<ForestBuilderOptions> = {},
  ): Promise<ForestBuilderCharacter> {
    const options: ForestBuilderOptions = { ...DEFAULT_FOREST_BUILDER_OPTIONS, ...partialOptions };
    const started = performance.now();

    let imported;
    try {
      imported = await ImportMeshAsync(options.source, scene, {
        // Les octets bruts ne portent pas d'extension : sans cette indication,
        // Babylon ne saurait pas quel loader employer.
        pluginExtension: ".glb",
        name: "forest-builder-v2.glb",
      });
    } catch (error) {
      throw new Error(
        `Personnage Forest Builder : chargement impossible ` +
          `(${typeof options.source === "string" ? options.source : "données binaires"}) — ` +
          describeError(error),
        { cause: error },
      );
    }

    // Les matrices des 22 os sont stockées dans une texture plutôt que dans
    // les uniformes du vertex shader. Cela évite de dépasser la limite des GPU
    // mobiles modestes tout en laissant de la place aux lumières et au PBR.
    for (const skeleton of imported.skeletons) {
      skeleton.useTextureToStoreBoneMatrices = true;
    }

    // La racine `__root__` est ajoutée par le chargeur glTF pour convertir le
    // repère droitier du format vers celui, gaucher, de Babylon. **Elle doit
    // rester dans la chaîne** : la contourner pour reparenter directement le
    // nœud `CharacterRoot` supprimerait cette conversion, et le personnage
    // regarderait vers −Z, l'inverse du sens documenté par l'asset.
    const visualRoot = imported.meshes.find((mesh) => mesh.parent === null);
    let controller: Mesh | undefined;

    try {
      if (!visualRoot) {
        throw new Error("Personnage Forest Builder : le GLB ne contient aucune racine.");
      }

      const animations = collectAnimations(imported.animationGroups);
      controller = ForestBuilderCharacter.createController(scene, options);

      visualRoot.parent = controller;
      // Le contrôleur a son origine au centre de la capsule, le modèle au niveau
      // des pieds : le modèle descend d'une demi-hauteur pour que les deux se
      // recouvrent.
      visualRoot.position.y = -options.capsuleHeight / 2;

      for (const mesh of imported.meshes) {
        // Les sous-maillages visuels ne participent ni aux collisions ni à la
        // sélection : seul le contrôleur porte le volume de collision, et un
        // clic dans le décor n'a aucune raison de heurter une manche de tunique.
        mesh.checkCollisions = false;
        mesh.isPickable = false;
        // Les pièces répétées du modèle (mèches de cheveux, lacets, yeux) sont
        // importées comme instances d'un même maillage : elles tiennent cette
        // propriété de leur source, et la leur fixer ne ferait qu'émettre un
        // avertissement Babylon à chaque chargement.
        if (!mesh.isAnInstance) mesh.receiveShadows = true;
      }

      logger.info(
        `Personnage Forest Builder prêt en ${Math.round(performance.now() - started)} ms ` +
          `(${imported.meshes.length} maillages, ${imported.animationGroups.length} animations).`,
      );

      return new ForestBuilderCharacter(controller, visualRoot, animations, options);
    } catch (error) {
      // Un GLB incomplet ne doit pas laisser derrière lui une moitié de
      // personnage dans la scène : ce qui a été importé est détruit avant que
      // l'erreur ne remonte. Détruire la capsule suffit dès qu'elle existe, le
      // modèle étant alors son enfant.
      if (controller) controller.dispose(false, true);
      else visualRoot?.dispose(false, true);
      for (const group of imported.animationGroups) group.dispose();
      throw error;
    }
  }

  /** Animation en cours de lecture. */
  get animation(): CharacterAnimation {
    return this.current;
  }

  /** Une action bloquante est en cours : le déplacement est refusé. */
  get busy(): boolean {
    return this.chopping;
  }

  /** Altitude du sol sous les pieds. Le contrôleur du jeu la fixe à chaque image. */
  get groundHeight(): number {
    return this.controller.position.y - this.options.capsuleHeight / 2;
  }

  set groundHeight(y: number) {
    this.controller.position.y = y + this.options.capsuleHeight / 2;
  }

  /** Cap du personnage, en radians. */
  get facing(): number {
    return this.controller.rotation.y;
  }

  /**
   * Applique une image de simulation : déplacement, cap et choix d'animation.
   *
   * Le déplacement passe par `moveWithCollisions`, qui fait glisser la capsule le
   * long des obstacles au lieu de l'y arrêter net. Il reste **horizontal** : la
   * verticale appartient au sol, que le jeu impose ensuite via `groundHeight`.
   */
  update(input: CharacterInput, deltaSeconds: number): void {
    // Pendant la coupe, le personnage est planté : ni pas, ni demi-tour. Rendre
    // la main immédiatement laisserait le joueur annuler l'action en poussant le
    // manche, et le geste n'aurait plus de poids.
    if (this.chopping) return;

    const { x, z } = input.direction;
    const length = Math.hypot(x, z);

    // Un cap imposé s'applique même à l'arrêt : c'est ce qui permet de pivoter
    // sur place, geste indispensable quand un seul manche sert à choisir le cap.
    // Un cap *déduit* du déplacement, lui, n'a pas de sens sans déplacement.
    if (input.facing !== undefined) this.controller.rotation.y = input.facing;

    if (length < MOVE_EPSILON) {
      this.playLoop("Idle", 1);
      return;
    }

    const intensity = Math.min(length, 1);
    const facing = input.facing ?? Math.atan2(x, z);
    this.controller.rotation.y = facing;

    const { walkSpeed, runSpeed } = this.options;
    // Un facteur négatif ferait reculer le personnage sans que son cycle
    // s'inverse : la vitesse ne se pilote que par la direction commandée.
    const scale = Math.max(input.speedScale ?? 1, 0);
    const speed = (input.running ? runSpeed : walkSpeed) * intensity * scale;
    this.step.set((x / length) * speed * deltaSeconds, 0, (z / length) * speed * deltaSeconds);
    this.controller.moveWithCollisions(this.step);

    // Le cycle joué et sa cadence découlent de la **vitesse réelle**, et non de
    // la commande : c'est la seule façon de garder les pieds au sol quand un
    // multiplicateur s'en mêle. Passé la vitesse de course, la marche n'a plus
    // de sens — un cycle de marche accéléré trois fois donnerait un personnage
    // en accéléré, là où la course, à peine pressée, reste une foulée.
    const running = input.running || speed > runSpeed;
    const cadence = speed / (running ? runSpeed : walkSpeed);

    // Marche arrière : le cycle se déroule à l'envers. Sans cela, le personnage
    // avancerait les jambes comme s'il allait de l'avant tout en reculant.
    const backwards = Math.sin(facing) * x + Math.cos(facing) * z < 0;

    this.playLoop(running ? "Run" : "Walk", Math.max(cadence, MIN_CADENCE) * (backwards ? -1 : 1));
  }

  /**
   * Joue la coupe une fois, puis revient à `Idle`.
   *
   * Rend `false` si une coupe est déjà en cours : la commande est ignorée plutôt
   * que mise en file, sinon maintenir la touche enfilerait les coups longtemps
   * après l'avoir relâchée.
   */
  chop(): boolean {
    if (this.chopping || this.disposed) return false;

    const { Chop } = this.animations;
    this.chopping = true;
    this.stopAll();
    this.current = "Chop";

    Chop.onAnimationGroupEndObservable.addOnce(() => {
      if (this.disposed) return;
      this.chopping = false;
      this.playLoop("Idle", 1);
    });
    Chop.start(false, 1, Chop.from, Chop.to, false);

    return true;
  }

  /** Affiche ou masque le personnage sans le détruire (pause, cinématique…). */
  setEnabled(enabled: boolean): void {
    this.controller.setEnabled(enabled);
  }

  /**
   * Libère tout ce que le personnage possède.
   *
   * Les groupes d'animation appartiennent à la scène, pas à la hiérarchie de
   * maillages : détruire la capsule et ses enfants les laisserait derrière, à
   * animer des cibles détruites. Ils sont donc détruits explicitement.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    for (const group of Object.values(this.animations)) {
      group.onAnimationGroupEndObservable.clear();
      group.stop(true);
      group.dispose();
    }

    this.controller.dispose(false, true);
  }

  private static createController(scene: Scene, options: ForestBuilderOptions): Mesh {
    const controller = new Mesh("forest-builder", scene);

    // `isVisible = false` plutôt que `visibility = 0` : la seconde forme laisse
    // le mesh dans la file de rendu, en transparence. Ici il n'y a de toute
    // façon aucune géométrie à dessiner — seul l'ellipsoïde compte.
    controller.isVisible = false;
    controller.isPickable = false;
    controller.checkCollisions = true;
    controller.ellipsoid = new Vector3(
      options.capsuleRadius,
      options.capsuleHeight / 2,
      options.capsuleRadius,
    );
    controller.position.y = options.capsuleHeight / 2;

    return controller;
  }

  /**
   * Lance un cycle, ou ajuste sa cadence s'il tourne déjà.
   *
   * Le second cas est celui de presque toutes les images : redémarrer le groupe
   * à chaque changement de cadence le remettrait à sa première pose, et le
   * personnage piétinerait sur place.
   */
  private playLoop(name: LoopedAnimation, speedRatio: number): void {
    const group = this.animations[name];

    if (this.current === name) {
      if (group.speedRatio !== speedRatio) group.speedRatio = speedRatio;
      return;
    }

    this.stopAll();
    group.start(true, speedRatio, group.from, group.to, false);
    this.current = name;
  }

  /**
   * `stop(true)` : l'arrêt ne notifie pas la fin du groupe. Sans ce drapeau,
   * couper une animation pour en lancer une autre déclencherait le rappel de fin
   * de la coupe, qui rendrait la main au joueur en plein geste.
   */
  private stopAll(): void {
    for (const group of Object.values(this.animations)) group.stop(true);
  }
}
